import { FC, Fragment, useEffect, useRef, useState } from 'react';

import { Checkbox } from '@telialt/common-checkbox/src';
import { escapeRegExp, isDefined } from '@telialt/utils/src/helpers';

import { CheckboxFilterTreeContainer } from './CheckboxFilterTree.styles';
import { FilterTreeSearch, FilterTreeSearchProps } from './FilterTreeSearch';
import { TextLabel } from './LabelComponents';
import {
    ControlledFilter,
    FilterNode,
    LabelComponent,
    RevealingListDepthVariants,
    SelectionBehaviorVariants,
} from './model';
import { RevealingList, RevealingListProps } from './RevealingList';
import {
    extractChecked,
    toControlledTree,
    toPathId,
    updateTreeCheckedStatusToLeaf,
    updateTreeCheckedStatusToRoot,
    updateTreeCheckedStatusToRootFuzzyBehavior,
    updateTreeCheckedStatusToRootSingleBehavior,
} from './utils';

type FilterId = FilterNode['title'];
type FilterSelectHandler = (ids: FilterId[][]) => void;
type Props = {
    /** Required data to display */
    filters: FilterNode[];
    /** An optional array of selected filter IDs, when not passed the component still works, but as uncontrolled input */
    selected?: FilterId[][];
    onSelect?: FilterSelectHandler;
    /** Defines the checkbox selection behavior. Defaults to 'classic' - a common known behavior. 'fuzzy' - is used when you might have undetermined amount of subcategories */
    selectionBehavior?: SelectionBehaviorVariants;
    revealingListDepth?: RevealingListDepthVariants;
    Label?: LabelComponent;
    className?: string;
    'data-test'?: string;
    'aria-label'?: string;
} & RevealingListProps &
    FilterTreeSearchProps;

export const CheckboxFilterTree: FC<Props> = ({
    filters = [],
    selected: initSelected,
    onSelect,
    className,
    selectionBehavior = 'classic',
    maxVisibleItems,
    isInitiallyExpanded,
    showLessText,
    showMoreText,
    revealingListDepth: listRevealingDepth = 'top-level',
    Label = TextLabel,
    searchEnabled = false,
    searchPlaceholder,
    searchAllyLabel,
    searchClearButtonAllyLabel = 'clear',
    'aria-label': ariaLabel,
    ...htmlContainerProps
}) => {
    const listProps = { isInitiallyExpanded, showLessText, showMoreText };
    const searchProps = { searchEnabled, searchPlaceholder, searchAllyLabel, searchClearButtonAllyLabel };

    const [tree, setTree] = useState(toControlledTree(filters, [], initSelected?.map(toPathId), selectionBehavior));
    const [searchWord, setSearchWord] = useState<string>('');
    const onSelectTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        setTree(toControlledTree(filters, [], initSelected?.map(toPathId), selectionBehavior, searchWord));
    }, [filters, initSelected, selectionBehavior, searchWord]);
    const handleCheckChange = (node: ControlledFilter) => {
        const checked = node.checked === 'indeterminate' ? true : !node.checked;
        node.checked = checked;

        let updatedTree: ControlledFilter[];

        if (selectionBehavior === 'classic') {
            if (node.filters?.length) {
                updateTreeCheckedStatusToLeaf(node.filters, checked);
            }
            updatedTree = updateTreeCheckedStatusToRoot(tree);
        }
        if (selectionBehavior === 'fuzzy') {
            updatedTree = updateTreeCheckedStatusToRootFuzzyBehavior(tree, false, checked ? node.pathId : undefined);
        }
        if (selectionBehavior === 'single') {
            updatedTree = updateTreeCheckedStatusToRootSingleBehavior(tree, checked ? node.pathId : undefined);
        }

        setTree(updatedTree);

        clearTimeout(onSelectTimeoutRef.current);
        onSelectTimeoutRef.current = setTimeout(() => {
            onSelect?.(extractChecked(updatedTree));
        }, 0);
    };

    useEffect(() => {
        return () => clearTimeout(onSelectTimeoutRef.current);
    }, []);

    const handleSearchChange = (keyword: string) => {
        setSearchWord(keyword);
    };

    const renderFilterNodes = (nodes: typeof tree = [], parentTitle?: string, isListDeeplyRevealable = true) => {
        if (!nodes.length) {
            return null;
        }

        return (
            <RevealingList
                {...listProps}
                maxVisibleItems={isListDeeplyRevealable ? maxVisibleItems : 'all'}
                aria-label={parentTitle}
            >
                {nodes.map((node) => {
                    const titleParts = node.searchWord
                        ? node.title.split(new RegExp(`(${escapeRegExp(node.searchWord)})`, 'gi'))
                        : [node.title];

                    const highlightedTitle = titleParts.map((part, index) =>
                        part.trim().toLowerCase() === node.searchWord?.trim().toLowerCase() ? (
                            <strong key={index}>{part}</strong>
                        ) : (
                            <Fragment key={index}>{part}</Fragment>
                        ),
                    );

                    return (
                        <div key={node.pathId} role="listitem" hidden={!node.visible}>
                            <Checkbox
                                aria-label={node.title + (isDefined(node.count) ? ` (${node.count})` : '')}
                                label={<Label title={highlightedTitle} count={node.count} />}
                                checked={node.checked}
                                onChange={() => handleCheckChange(node)}
                                dataTest={node.pathId}
                            />
                            {renderFilterNodes(node.filters, node.title, listRevealingDepth === 'all-levels')}
                        </div>
                    );
                })}
            </RevealingList>
        );
    };

    return (
        <CheckboxFilterTreeContainer className={className} {...htmlContainerProps}>
            <FilterTreeSearch {...searchProps} onSearchChange={handleSearchChange} />
            {renderFilterNodes(tree, ariaLabel)}
        </CheckboxFilterTreeContainer>
    );
};
